import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FieldChips, type ChipItem, type ChipOption, type FieldChipsState } from "@/components/ui/FieldChips";
import { supabase } from "@/lib/supabase-client";

interface PositionRef {
  id: string;
  name: string;
  description?: string;
  billing_rate?: number;
}

type PayRate = { rate: "hourly"; amount: number };

type PositionValue = {
  position: PositionRef;
  meta_data?: { pay_rate?: PayRate };
};

interface PositionsInputProps {
  /** Entity's own positions — array of { position, meta_data? } */
  value: PositionValue[];
  state?: FieldChipsState;
  label?: string;
  error?: string;
  onChange?: (positions: PositionValue[]) => void;
}

export function PositionsInput({
  value,
  state = "editing",
  label = "Positions",
  error,
  onChange,
}: PositionsInputProps) {
  const [options, setOptions] = useState<ChipOption[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch all position options on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const { data, error: fetchError } = await supabase
          .schema("lists")
          .from("positions")
          .select("id, name, description, billing_rate")
          .order("name", { ascending: true });
        if (fetchError) throw fetchError;
        if (cancelled) return;
        setOptions(
          (data ?? []).map((p) => ({
            id: p.id,
            name: p.name,
            description: p.description || undefined,
          })),
        );
      } catch (err) {
        console.error("Failed to load positions:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Build chip items from value
  const chipItems = useMemo<ChipItem[]>(() => {
    return value.map((up) => ({
      id: up.position.id,
      name: up.position.name,
      subtitle: up.position.description,
      readonly: false,
      rate: up.meta_data?.pay_rate?.amount ?? null,
    }));
  }, [value]);

  const handleAdd = useCallback(
    (positionId: string) => {
      if (value.some((up) => up.position.id === positionId)) return;
      const opt = options.find((o) => o.id === positionId);
      if (!opt) return;
      onChange?.([...value, { position: { id: opt.id, name: opt.name, description: opt.description } }]);
    },
    [value, options, onChange],
  );

  const handleRemove = useCallback(
    (chipId: string) => {
      onChange?.(value.filter((up) => up.position.id !== chipId));
    },
    [value, onChange],
  );

  const handleRateChange = useCallback(
    (chipId: string, rate: number | null) => {
      onChange?.(
        value.map((up) =>
          up.position.id === chipId
            ? {
                ...up,
                meta_data:
                  rate != null
                    ? { ...up.meta_data, pay_rate: { rate: "hourly" as const, amount: rate } }
                    : { ...up.meta_data, pay_rate: undefined },
              }
            : up,
        ),
      );
    },
    [value, onChange],
  );

  const resolvedState: FieldChipsState = loading ? "loading" : state;

  return (
    <FieldChips
      label={label}
      icon="briefcase-outline"
      accentTone="violet"
      items={chipItems}
      options={options}
      state={resolvedState}
      placeholder="Search positions…"
      error={error}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onRateChange={handleRateChange}
    />
  );
}
